import { WaitlistForm } from './components/WaitlistForm';

function App() {
  const apiUrl = import.meta.env.VITE_API_URL || '';

  return (
    <div className="page">
      {/* Nav */}
      <div className="nav-wrapper">
        <nav className="nav">
          <span className="nav-logo">
            <img src="/favicon.svg" alt="" className="nav-logo-icon" />
            <em>Claryn</em>{' '}Health
          </span>
          <a href="#join" className="nav-cta">Join Waitlist</a>
        </nav>
      </div>

      {/* Section 1: Hero */}
      <section className="hero" id="join">
        <div className="container">
          <h1 className="hero-headline">
            Stop Reconciling Tabs.<br />Start Signing Off.
          </h1>
          <p className="hero-sub">
            <em>Claryn</em> is the workflow-native clinical intelligence layer that
            reconciles fragmented labs, meds, imaging, and patient history
            into a safe, billable sign-off, directly inside Practice Fusion.
          </p>
          <div className="trust-line">
            <span>No audio recording</span>
            <span className="trust-sep">&middot;</span>
            <span>No new logins</span>
            <span className="trust-sep">&middot;</span>
            <span>Clinician remains in control</span>
          </div>

          {/* TODO: Validate this claim with pilot data */}
          <div className="metric-row">
            <div className="metric-item">
              <div className="metric-value">~15 min</div>
              <div className="metric-label">saved per complex encounter</div>
            </div>
          </div>

          <WaitlistForm apiUrl={apiUrl} buttonText="Join the Waitlist" />
          <p className="hero-microcopy">
            You stay in control. Every suggestion is editable.
          </p>
        </div>
      </section>

      {/* Section 2: How It Works */}
      <section className="section section-alt">
        <div className="container">
          <div className="how-header">
            <h2 className="section-title">How <em>Claryn</em> Supports Sign-Off in Real Time</h2>
            <p className="section-subtitle">The Smart Clipboard for Complex Charts.</p>
            <p className="section-desc">
              <em>Claryn</em> lives in your browser, capturing the clinical data you
              already review so you never have to hunt for it twice.
            </p>
          </div>

          <div className="steps">
            <div className="step">
              <div className="step-number">1</div>
              <div className="step-label">Identify</div>
              <h3 className="step-title">Real-Time Capture</h3>
              <p>
                When you open labs, imaging, or document tabs in Practice Fusion, <em>Claryn</em> captures key structured data into your side panel as you
                review each screen.
              </p>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <div className="step-label">Reconcile</div>
              <h3 className="step-title">Structured Clinical View</h3>
              <p>
                <em>Claryn</em> organizes captured data into a structured summary to
                support Assessment &amp; Plan documentation.
              </p>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <div className="step-label">Sign Off</div>
              <h3 className="step-title">Sign-Off Support</h3>
              <p>
                Flags quality and screening gaps and prepares reconciled content
                for one-click copy or insert into your note.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Labs to Sign-Off Detail */}
      <section className="section">
        <div className="container">
          <div className="two-col">
            <div className="two-col-text">
              <h2 className="section-title">From Raw Labs to Structured Sign-Off Support</h2>
              <p>
                When clinicians review labs, <em>Claryn</em> captures key values, checks
                diagnosis presence, and prepares structured assessment and plan
                scaffolds for clinician review and insertion, accelerating
                reconciliation without automating clinical judgment.
              </p>
              <div className="tag-row">
                <span className="tag">ICD support</span>
                <span className="tag">Diagnosis suggestions</span>
                <span className="tag">Dx presence checks</span>
              </div>
            </div>
            <div>
              {/* Mockup: Side panel preview */}
              <div className="mockup">
                <div className="mockup-header">Patient Context</div>
                <div className="mockup-body">
                  <div className="mockup-list-item">
                    <span className="mockup-check">{"\u2713"}</span>
                    <span>Prior pelvic ultrasound (2022) reviewed &mdash; Negative</span>
                  </div>
                  <div className="mockup-list-item">
                    <span className="mockup-check" style={{ color: 'var(--red-muted)' }}>!</span>
                    <span>Follow-up recommended: Confirm symptom resolution</span>
                  </div>
                  <div className="mockup-list-item">
                    <span className="mockup-check" style={{ color: 'var(--red-muted)' }}>!</span>
                    <span>Bariatric referral detected &mdash; Consider metabolic risk review</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Clinical & Practice Impact */}
      <section className="section section-alt">
        <div className="container">
          <div className="impact-header">
            <h2 className="section-title">
              Reduce Documentation Drag. Protect Billable Accuracy.
            </h2>
          </div>

          <div className="impact-grid">
            {/* Card 1 */}
            <div className="impact-card">
              <div className="impact-card-body">
                <h3 className="impact-card-title">Reduce After-Hours Charting</h3>
                <p>
                  By reconciling data during the visit, <em>Claryn</em> helps clinicians
                  complete documentation closer to the point of care.
                </p>
              </div>
              <div className="impact-card-visual">
                <div className="mockup">
                  <div className="mockup-header">Clinical Correlation Detected</div>
                  <div className="mockup-body">
                    <div className="mockup-list-item">Lab abnormalities explain reported fatigue.</div>
                    <div className="mockup-list-item">Metabolic risk profile emerging.</div>
                    <div className="mockup-list-item">Prior diverticulosis relevant to LLQ pain.</div>
                  </div>
                </div>
                <div className="mockup">
                  <div className="mockup-header">Billing Readiness</div>
                  <div className="mockup-body">
                    <div className="mockup-list-item">
                      <span className="mockup-check">{"\u2713"}</span>
                      <span>Documentation supports CPT 99396</span>
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontWeight: 500 }}>Chart Readiness</span>
                        <span style={{ fontWeight: 600 }}>92%</span>
                      </div>
                      <div className="mockup-progress"><div className="mockup-progress-bar" style={{ width: '92%' }} /></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 2 */}
            <div className="impact-card">
              <div className="impact-card-body">
                <h3 className="impact-card-title">Reduce Sign-Off Friction</h3>
                <p>
                  <em>Claryn</em> reconciles labs, imaging, and history into structured
                  assessment and plan suggestions with ICD-10 codes, ready for
                  one-click copy into your note.
                </p>
              </div>
              <div className="impact-card-visual">
                <div className="mockup">
                  <div className="mockup-header">
                    <span>Suggested Assessment (5)</span>
                    <span className="copy-btn copy-btn-label">Copy All</span>
                  </div>
                  <div className="mockup-body">
                    <div className="mockup-list-item">Z00.00 &mdash; Well visit</div>
                    <div className="mockup-list-item">E55.9 &mdash; Vitamin D deficiency</div>
                    <div className="mockup-list-item">D50.9 &mdash; Iron deficiency anemia</div>
                    <div className="mockup-list-item">E78.1 &mdash; Hypertriglyceridemia</div>
                    <div className="mockup-list-item">R53.83 &mdash; Fatigue (supporting)</div>
                  </div>
                </div>
                <div className="mockup">
                  <div className="mockup-header">
                    <span>Recommended Plan (5)</span>
                    <span className="copy-btn copy-btn-label">Copy All</span>
                  </div>
                  <div className="mockup-body">
                    <div className="mockup-list-item">1. Vitamin D3 supplementation</div>
                    <div className="mockup-list-item">2. Begin oral iron supplementation</div>
                    <div className="mockup-list-item">3. Labs: CBC, iron studies, Vitamin D, lipid panel</div>
                    <div className="mockup-list-item">4. Lifestyle counseling; weight reduction</div>
                    <div className="mockup-list-item">5. f/u in 3 mos for repeat labs</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 3 */}
            <div className="impact-card">
              <div className="impact-card-body">
                <h3 className="impact-card-title">Quality &amp; Screening Support</h3>
                <p>
                  Links plan elements to reviewed chart data and highlights
                  quality measure gaps.
                </p>
              </div>
              <div className="impact-card-visual">
                <div className="mockup">
                  <div className="mockup-header">Lab Intelligence (2022 &rarr; 2026)</div>
                  <div className="mockup-body">
                    <div className="mockup-indicator red">Vitamin D: 24 &rarr; 12.5 &mdash; Severe deficiency</div>
                    <div className="mockup-indicator red">Triglycerides: 198 &rarr; 289 &mdash; Worsening</div>
                    <div className="mockup-indicator orange">Iron Status: Ferritin &darr;, Transferrin Sat &darr;</div>
                    <div className="mockup-indicator green">Stable: A1c &middot; TSH &middot; CMP normal</div>
                  </div>
                </div>
                <div className="mockup">
                  <div className="mockup-header">Preventive &amp; Screening</div>
                  <div className="mockup-body">
                    <div className="mockup-list-item">
                      <span className="mockup-check" style={{ color: 'var(--red-muted)' }}>!</span>
                      <span>Mammogram Recommended (due 2026)</span>
                    </div>
                    <div className="mockup-list-item">
                      <span className="mockup-check" style={{ color: 'var(--red-muted)' }}>!</span>
                      <span>Hypertension education &mdash; Positive (based on vitals)</span>
                    </div>
                    <div className="mockup-list-item">
                      <span className="mockup-check" style={{ color: 'var(--red-muted)' }}>!</span>
                      <span>BMI &mdash; Positive (based on vitals)</span>
                    </div>
                    <div className="mockup-list-item">
                      <span className="mockup-check">{"\u2713"}</span>
                      <span>Cervical cancer screening results documented</span>
                    </div>
                    <div className="mockup-list-item">
                      <span className="mockup-check">{"\u2713"}</span>
                      <span>Colorectal cancer screening results documented</span>
                    </div>
                    <div className="mockup-list-item">
                      <span className="mockup-check">{"\u2713"}</span>
                      <span>Assessment using PHQ-2 questionnaire &rarr; 0</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: Focus */}
      <section className="section">
        <div className="container">
          <div className="focus-text">
            <h2 className="section-title">
              Built for Independent Clinicians. Starting with Practice Fusion.
            </h2>
            <p>
              <em>Claryn</em> begins with Practice Fusion workflows, where documentation
              fragmentation is high and tooling is limited. This gives us a fast,
              low-integration starting point to validate workflow-native clinical
              intelligence in real outpatient settings before expanding across
              ambulatory EHR platforms.
            </p>
          </div>
        </div>
      </section>

      {/* Section: Why Now */}
      <section className="section section-alt">
        <div className="container">
          <div className="why-now-text">
            <h2 className="section-title">Why Now</h2>
            <p>
              Practice Fusion supports over 112,000 providers. Native AI scribes
              focus on documenting the conversation, not supporting chart
              workflows. <em>Claryn</em>'s browser-based extension bridges this gap,
              structuring clinical data in real time without complex EHR
              integrations. A copilot for clinical workflow.
            </p>
          </div>
          <div className="why-now-points">
            <div className="why-now-point">
              <h3>Ambient AI Only</h3>
              <p>
                Practice Fusion has no built-in reconciliation or sign-off
                intelligence, leaving clinicians to do it manually.
              </p>
            </div>
            <div className="why-now-point">
              <h3>Browser-Native Access</h3>
              <p>
                Modern browser extensions can read structured clinical data
                directly from web-based EHRs without vendor integration.
              </p>
            </div>
            <div className="why-now-point">
              <h3>Underserved Segment</h3>
              <p>
                Independent practices lack the IT resources for enterprise AI
                tools. A lightweight, zero-integration layer meets them where
                they are.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 5: Trust */}
      <section className="section section-navy">
        <div className="container">
          <div style={{ textAlign: 'center' }}>
            <h2 className="section-title">Clinical Intelligence You Can Trust</h2>
          </div>
          <div className="trust-grid">
            <div className="trust-item">
              <h3 className="trust-item-title">Privacy by Design</h3>
              <p>
                <em>Claryn</em> processes only the data visible in your workflow session
                and does not store patient charts outside the active session.
              </p>
            </div>
            <div className="trust-item">
              <h3 className="trust-item-title">No Model Training on Patient Data</h3>
              <p>
                Customer data is never used to train foundation models.
              </p>
            </div>
            <div className="trust-item">
              <h3 className="trust-item-title">Human in the Loop</h3>
              <p>
                No note content is finalized without clinician review and approval.
              </p>
            </div>
            <div className="trust-item">
              <h3 className="trust-item-title">Security-First Architecture</h3>
              <p>
                Encrypted in transit and at rest. Built with HIPAA-aligned safeguards.
              </p>
            </div>
          </div>

          {/* TODO: Review badge language with compliance/legal */}
          <div className="compliance-row">
            <div className="compliance-badge">
              <svg className="compliance-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              HIPAA-Aligned Architecture
            </div>
            <div className="compliance-badge">
              <svg className="compliance-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
              Built for Practice Fusion Workflows
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta">
        <div className="container">
          <h2 className="section-title">Join the Waitlist</h2>
          <p className="section-desc">
            Be among the first independent practices testing workflow-native
            clinical intelligence at sign-off.
          </p>
          <WaitlistForm apiUrl={apiUrl} buttonText="Join the Waitlist" />
          <p className="hero-microcopy">
            No audio recording &middot; Clinicians remain in control
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="section section-alt">
        <div className="container">
          <div className="mission-text">
            <h2 className="section-title">Mission: Claryn Health</h2>
            <p>
              <em>Claryn</em> was born from firsthand outpatient charting experience
              and seeing how much work it takes to turn reviewed chart data into
              complete, sign-off-ready notes.
            </p>
            <p>
              Clinicians don't struggle with clinical thinking. They struggle
              with the documentation load that follows it.
            </p>
            <p>
              <em>Claryn</em> is a workflow-native clinical copilot that organizes
              chart data as you work so documentation gets completed with less
              effort, without recordings, workflow disruption, or replacing
              clinical judgment.
            </p>
          </div>

          {/* TEMP: second founder card removed; single-column override until restored */}
          <div className="founders-grid" style={{ gridTemplateColumns: '1fr', maxWidth: 360 }}>
            <div className="founder-card">
              <img src="/steph-villa.jpg" alt="Steph Villa" className="founder-avatar" />
              <div className="founder-name">Steph Villa</div>
              <div className="founder-role">Clinical &amp; Product</div>
              <p className="founder-bio">
                5+ years inside outpatient clinical workflows across medical
                scribing and clinical training. Built from firsthand
                documentation experience.
              </p>
            </div>
            {/* TEMP: removed, restore with the grid override above
            <div className="founder-card">
              <img src="/nate-fine.jpg" alt="Nate Fine" className="founder-avatar" />
              <div className="founder-name">Nate Fine</div>
              <div className="founder-role">Technical</div>
              <p className="founder-bio">
                8 years of engineering experience at companies including
                Amazon and Apple. Building the infrastructure behind
                workflow-native clinical intelligence.
              </p>
            </div>
            */}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <a href="mailto:hello@clarynhealth.com" className="footer-contact-link">
            <svg className="footer-contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
            Get in touch with us!
          </a>
          <div>&copy; {new Date().getFullYear()} Claryn Health. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
}

export default App;
